import classNames from "classnames/dedupe";
import React, { FC, useCallback, useEffect, useMemo } from "react";

import RangeInput, {
  TRangeTuple,
} from "../../../ActionsSelectors/RangeInput/RangeInput";
import { useTheme } from "../../../Utility/ThemeProvider";
import { IAbstractFilterProps, AbstractFilter } from "../AbstractFilter";
import { IFilterUpdate, IRangeFilterData } from "../Filter.interface";
import { useWatchState } from "../../../../hooks";
import { deepClone } from "../../../../helpers";
import { IFilterTagData } from "../../FilterTags";

export interface IRangeFilterProp extends IAbstractFilterProps {
  /** Filter data items
   * @type {IRangeFilterData}
   */
  filterData: IRangeFilterData;
  /**
   * A callback function for when this component is changing range input state
   */

  /** Position of currency text */
  currencyPos?: "left" | "right";

  /** Callback that returns set of values and what exactly was updated */
  onChange?(data: { values: TRangeTuple; updates: IFilterUpdate[] }): void;

  /** onAfterChange function triggers after user release of measure element */
  onAfterChange?(data: { values: TRangeTuple; updates: IFilterUpdate[] }): void;
}

export const RANGE_FILTER_EMPTY_STATE = ["", ""] as TRangeTuple;

export const resetRangeFilter = (
  filterData: IRangeFilterData,
): IRangeFilterData => {
  const newData = deepClone(filterData);
  if (newData.defaultValue) {
    newData.value = deepClone(newData.defaultValue);
  } else {
    newData.value = deepClone(RANGE_FILTER_EMPTY_STATE);
  }
  return newData;
};

export const rangeFilterItemToTagsDTO = ({
  value,
  defaultValue,
  name,
  titles,
  currency,
}: IRangeFilterData): IFilterTagData[] => {
  const result = [];

  if (defaultValue?.[0] && defaultValue[0] !== value[0]) {
    result[0] = {
      id: "0",
      category: name,
      text: `${titles?.min || ""} ${value[0]} ${currency || ""}`,
    };
  }

  if (defaultValue?.[1] && defaultValue[1] !== value[1]) {
    result[1] = {
      id: "1",
      category: name,
      text: `${titles?.max || ""} ${value[1]} ${currency || ""}`,
    };
  }

  return result;
};

const RangeFilter: FC<IRangeFilterProp> = ({
  filterData,
  variant,
  currencyPos = filterData.currencyPos || "left",
  onChange,
  onAfterChange,
  ...abstractFilterProps
}) => {
  const [range, setRange] = useWatchState<TRangeTuple>({
    value: (filterData.value as TRangeTuple) ?? RANGE_FILTER_EMPTY_STATE,
  });
  useEffect(() => {
    setRange(filterData.value ?? RANGE_FILTER_EMPTY_STATE);
  }, [filterData]);

  const handleChange = useCallback(
    (values: [number, number]) => {
      const StringedValues = values.map((v) => String(v)) as TRangeTuple;
      setRange(StringedValues);
      onChange?.({
        values: StringedValues as TRangeTuple,
        updates: [{ fieldName: filterData.name, value: values }],
      });
    },
    [filterData.name, onChange, setRange],
  );

  const handleAfterChange = useCallback(
    (values: [number, number]) => {
      onAfterChange?.({
        values: values.map((v) => String(v)) as TRangeTuple,
        updates: [{ fieldName: filterData.name, value: values }],
      });
    },
    [onAfterChange],
  );

  const theme = useTheme(variant);

  const rootClasses = classNames("filter-form-price", {
    "filter-form-price--light": theme === "light",
  });

  const numberValues = useMemo(() => {
    const values = range?.map?.((it) => (it ? Number(it) : 0)) ?? [];

    return values.every((v) => !v) ? undefined : values;
  }, [range]);
  const defaultValue = useMemo(
    () =>
      filterData?.defaultValue?.map?.((i) => Number(i)) || [
        Number(filterData.min),
        Number(filterData.max),
      ],
    [filterData.defaultValue, filterData.min, filterData.max],
  );
  const selectedRange = useMemo(() => {
    const [numberValue1, numberValue2] = numberValues ?? [];
    const [defValue1, defValue2] = defaultValue ?? [];

    return `${numberValue1 ?? defValue1}-${numberValue2 ?? defValue2}`;
  }, [numberValues, defaultValue]);

  const hasFromDesc =
    range[0] &&
    (!filterData.defaultValue?.[0] ||
      filterData.defaultValue?.[0] !== range[0]);
  const hasToDesc =
    range[1] &&
    (!filterData.defaultValue?.[1] ||
      filterData.defaultValue?.[1] !== range[1]);

  return (
    <AbstractFilter
      title={filterData.categoryName}
      variant={variant}
      badge={
        (
          ["dropdown-left", "dropdown-right"] as Array<
            IAbstractFilterProps["filterType"]
          >
        ).includes(abstractFilterProps.filterType)
          ? {
              count: Number(hasFromDesc) + Number(hasToDesc),
            }
          : null
      }
      {...abstractFilterProps}
    >
      <div className={rootClasses}>
        <div className="filter-form-price__title">
          {currencyPos === "left" ? `${filterData?.currency} ` : null}
          {selectedRange}
          {currencyPos === "right" ? ` ${filterData?.currency}` : null}
        </div>
        <div className="filter-form-price__body">
          <RangeInput
            variant={theme}
            min={Number(filterData.min)}
            max={Number(filterData.max)}
            value={numberValues}
            defaultValue={defaultValue}
            onAfterChange={handleAfterChange}
            onChange={handleChange}
          />
        </div>
      </div>
    </AbstractFilter>
  );
};

export default RangeFilter;
